// usePdfFlip.js
// IOS Form draait bij Next/Previous door als een kaart om zijn verticale as
// (zie flip-prop van PdfCanvasViewer). Los van CaseDetailDrawer zodat de
// timing offline te testen is met precies deze code.
import { useState, useRef, useEffect, useCallback } from "react";
import { PDF_FLIP } from "../../featureFlags";

// De oude pagina draait (versnellend) tot hij op zijn kant staat en dus
// vanzelf onzichtbaar is, de nieuwe draait in dezelfde richting verder vanaf
// de andere kant en veert even na. Geen fade. Aan/uit: PDF_FLIP in
// featureFlags.js.
const PDF_FLIP_OUT_MS = 400; // klaar vlak voordat de kaart weer infadet
const PDF_FLIP_IN_MS = 650;
const PDF_FLIP_OUT_EASE = "cubic-bezier(0.55, 0, 1, 0.45)"; // versnellen
const PDF_FLIP_IN_EASE = "cubic-bezier(0.2, 1.25, 0.4, 1)"; // snel in, zachte overshoot
const pdfFlipPose = (side) => `rotateY(${side * 90}deg)`;
// Draai vooraf ingepland i.p.v. stap voor stap via timers/renders: de ghost
// draait in SEAM_MS naar zijn kant (eindigt mét snelheid, niet loodrecht), en
// het nieuwe pdf start op precies dat moment (Web Animations met startTime op
// dezelfde klok) en draait met dezelfde snelheid verder. De browser speelt
// beide zelf af, dus een drukke main thread (zware render bij case-wissel)
// laat de draai niet meer haperen. Is het nieuwe pdf pas ná SEAM_MS klaar, dan draait het in zodra
// het klaar is. Op false = oude gedrag.
const PDF_FLIP_SCHEDULED = true;
// Iets na het moment dat de kaart weer infadet (450ms): wat marge zodat het
// nieuwe pdf er (bijna) altijd al is
const PDF_FLIP_SEAM_MS = 480;
// Eindsnelheid ghost = 90/480 * (1-0.6)/(1-0.85) = 0.50°/ms; beginsnelheid
// nieuw = 90/650 * 1.23/0.34 = 0.50°/ms -> geen knik op de naad
const PDF_FLIP_GHOST_EASE = "cubic-bezier(0.5, 0, 0.85, 0.6)";
const PDF_FLIP_SCHEDULED_IN_EASE = "cubic-bezier(0.34, 1.23, 0.4, 1)";
// Het indraaien stoppen op het moment dat de pagina precies plat staat (na
// ~310ms), i.p.v. nog ~340ms na te veren binnen 1,5°. Dat naveren zie je
// nauwelijks, maar Chrome toont de pagina zolang hij ook maar een fractie
// gedraaid staat opnieuw bemonsterd (= zacht); pas als de animatie klaar is
// wordt hij scherp. Begin en snelheid blijven gelijk (naad met de ghost).
// Op false = oude gedrag (hele 650ms met naveren).
const PDF_FLIP_IN_STOP_FLAT = true;
// Moment (ms) waarop cubic-bezier(0.34, 1.23, 0.4, 1) voor het eerst 1 is
const flatAtMs = (() => {
  const [p1x, p1y, p2x, p2y] = [0.34, 1.23, 0.4, 1];
  const bx = (t) => 3 * (1 - t) ** 2 * t * p1x + 3 * (1 - t) * t * t * p2x + t ** 3;
  const by = (t) => 3 * (1 - t) ** 2 * t * p1y + 3 * (1 - t) * t * t * p2y + t ** 3;
  let t = 0;
  while (t < 1 && by(t) < 1) t += 0.0005;
  return bx(t) * PDF_FLIP_IN_MS;
})();

/**
 * idle -> out -> in -> idle. dir: +1 = Next, -1 = Previous.
 * out: een vaste kopie ("ghost", zie PdfCanvasViewer) van de pagina's zoals
 *   ze bij de klik in beeld stonden draait weg. De live viewer staat al
 *   onzichtbaar op zijn kant aan de andere kant en laadt daar intussen het
 *   nieuwe pdf — wat daar getekend wordt is dus nooit op de wegdraaiende
 *   pagina te zien.
 * in: zodra de ghost op zijn kant staat én het nieuwe pdf klaar is, draait
 *   de live viewer in dezelfde richting verder naar het midden.
 * Alleen als het IOS Form in beeld is (active).
 *
 * @returns {{ phase, startPdfFlip(dir), flip }} flip = prop voor PdfCanvasViewer
 */
export function usePdfFlip({ active, hasPdf, caseId, viewer }) {
  const [pdfFlip, setPdfFlip] = useState({ phase: "idle", dir: 1, fromCaseId: null, seq: 0, t0: 0, inAt: 0 });
  const allowedRef = useRef(false);
  allowedRef.current = PDF_FLIP && active && hasPdf
    && !(typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);
  const caseIdRef = useRef(null);
  caseIdRef.current = caseId ?? null;
  const startPdfFlip = useCallback((dir) => {
    if (!allowedRef.current) return;
    setPdfFlip((f) => (f.phase === "out"
      // Snel nog eens geklikt: lopende draai gewoon afmaken en blijven wachten
      // op een case ná de oorspronkelijke
      ? f
      : { phase: "out", dir, fromCaseId: caseIdRef.current, seq: f.seq + 1, t0: performance.now(), inAt: 0 }));
  }, []);
  // Ghost staat op zijn kant: pas dan mag de nieuwe beginnen
  const [outDone, setOutDone] = useState(false);
  useEffect(() => {
    if (pdfFlip.phase !== "out") return undefined;
    setOutDone(false);
    const t = setTimeout(() => setOutDone(true), PDF_FLIP_OUT_MS);
    return () => clearTimeout(t);
  }, [pdfFlip.phase, pdfFlip.seq]);
  const ready = viewer.contentReady || viewer.status === "error";
  useEffect(() => {
    if (!PDF_FLIP || pdfFlip.phase !== "out") return undefined;
    // Andere tab gekozen: geen flip meer, gewoon de normale weergave
    if (!active) { setPdfFlip((f) => ({ ...f, phase: "idle" })); return undefined; }
    const switched = caseId !== pdfFlip.fromCaseId;
    // Geen pdf bij de nieuwe case: klaar
    if (switched && !hasPdf) { setPdfFlip((f) => ({ ...f, phase: "idle" })); return undefined; }
    if (switched && ready && PDF_FLIP_SCHEDULED) {
      // Klaar vóór de naad: met delay inplannen, zodat 'ie precies aansluit
      setPdfFlip((f) => ({ ...f, phase: "in", inAt: Math.max(f.t0 + PDF_FLIP_SEAM_MS, performance.now()) }));
      return undefined;
    }
    if (switched && ready && outDone) { setPdfFlip((f) => ({ ...f, phase: "in" })); return undefined; }
    // Vangnet: navigatie ging toch niet door (bv. einde van de lijst)
    const t = setTimeout(() => setPdfFlip((f) => (f.phase === "out" ? { ...f, phase: "in" } : f)), switched ? 15000 : 2000);
    return () => clearTimeout(t);
  }, [pdfFlip.phase, pdfFlip.fromCaseId, caseId, hasPdf, ready, active, outDone]);
  useEffect(() => {
    if (pdfFlip.phase !== "in") return undefined;
    const ms = PDF_FLIP_SCHEDULED && pdfFlip.inAt
      ? pdfFlip.inAt + PDF_FLIP_IN_MS + 50 - performance.now()
      : PDF_FLIP_IN_MS;
    const t = setTimeout(() => setPdfFlip((f) => (f.phase === "in" ? { ...f, phase: "idle" } : f)), ms);
    return () => clearTimeout(t);
  }, [pdfFlip.phase, pdfFlip.inAt]);
  const flip = !PDF_FLIP || pdfFlip.phase === "idle" ? null : {
    phase: pdfFlip.phase,
    seq: pdfFlip.seq,
    ghostTransform: pdfFlipPose(-pdfFlip.dir),
    ghostTransition: `transform ${PDF_FLIP_OUT_MS}ms ${PDF_FLIP_OUT_EASE}`,
    ghostMs: PDF_FLIP_OUT_MS,
    // PDF_FLIP_SCHEDULED: Web Animations i.p.v. css-transitions (zie
    // PdfCanvasViewer); startAt in performance.now()-tijd
    ghostAnim: PDF_FLIP_SCHEDULED
      ? { to: pdfFlipPose(-pdfFlip.dir), duration: PDF_FLIP_SEAM_MS, easing: PDF_FLIP_GHOST_EASE, startAt: pdfFlip.t0 }
      : null,
    inAnim: PDF_FLIP_SCHEDULED && pdfFlip.phase === "in"
      ? { from: pdfFlipPose(pdfFlip.dir), fromDeg: pdfFlip.dir * 90, duration: PDF_FLIP_IN_MS, easing: PDF_FLIP_SCHEDULED_IN_EASE, startAt: pdfFlip.inAt,
        // negatieve endDelay kapt de animatie af; daarna geldt transform: none
        endDelay: PDF_FLIP_IN_STOP_FLAT ? flatAtMs - PDF_FLIP_IN_MS : 0 }
      : null,
    live: pdfFlip.phase === "out"
      ? { transform: pdfFlipPose(pdfFlip.dir), opacity: 0, transition: "none" }
      // Alleen zichtbaar als het gevraagde document echt klaar is
      : {
        transform: "none",
        opacity: viewer.contentReady ? 1 : 0,
        transition: PDF_FLIP_SCHEDULED ? "none" : `transform ${PDF_FLIP_IN_MS}ms ${PDF_FLIP_IN_EASE}`,
      },
  };
  return { phase: pdfFlip.phase, startPdfFlip, flip };
}
